import { useState, useEffect, useRef } from 'react';
import { MdInfo, MdEvent, MdCampaign, MdWarning, MdDelete, MdPeople, MdPerson, MdMoreHoriz } from 'react-icons/md';
import { adToBs } from '../../utils/calendar';
import useAuth from '../../hooks/useAuth';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';

// One reusable, theme-aware announcement card used by the admin, student, and trainer
// views. Pass `onDelete` only for admins — students/trainers omit it, so no delete shows.

// Per-type: a saturated 3px left accent bar + a soft, low-opacity tinted badge.
// The bg-{color}-500/15 tints composite over bg-card, so they adapt to light/dark
// automatically; the saturated text/icon color stays legible in both modes — a
// restrained, premium look rather than a solid pastel block.
const TYPE_META = {
  general: { label: 'General', accent: 'bg-slate-500',  chip: 'bg-slate-500/15 text-muted',       Icon: MdInfo },
  holiday: { label: 'Holiday', accent: 'bg-amber-600',  chip: 'bg-amber-500/15 text-amber-600',   Icon: MdEvent },
  event:   { label: 'Event',   accent: 'bg-indigo-600', chip: 'bg-indigo-500/15 text-indigo-500', Icon: MdCampaign },
  urgent:  { label: 'Urgent',  accent: 'bg-red-600',    chip: 'bg-red-500/15 text-red-600',       Icon: MdWarning },
};

const AUDIENCE_LABEL = { all: 'Everyone', students: 'Students', trainers: 'Trainers' };

// "3 May 2026"
const fmtAd = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
// "Jestha 21, 2083" — reuses the app's BS converter (adToBs), no new date lib.
const fmtBs = (d) => {
  try { const b = adToBs(d); return `${b.monthName} ${b.day}, ${b.year}`; }
  catch { return fmtAd(d); }
};

// Footer timestamp: recent (<7 days) → relative; older → BS · AD date.
const fmtTimestamp = (raw) => {
  if (!raw) return '';
  const d   = new Date(raw);
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1)  return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)  return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return `${fmtBs(d)} · ${fmtAd(d)}`;
};

// Always-available full BS + AD date AND time, for the hover tooltip.
const fmtFull = (raw) => {
  if (!raw) return '';
  const d    = new Date(raw);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${fmtBs(d)} · ${fmtAd(d)} · ${time}`;
};

function AnnouncementCard({ announcement: a, onDelete }) {
  const { user } = useAuth();
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  // Same Primary-Admin gate as the fee-waive action. Delete is primary-admin only;
  // staff admins, trainers, and students get no delete control at all.
  const isPrimary = user?.adminType === 'primary' || user?.isPrimaryAdmin === true;
  const canDelete = !!onDelete && isPrimary;

  const [confirmDelete, setConfirm] = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [menuPos, setMenuPos]       = useState({ top: 0, left: 0 });
  const btnRef  = useRef(null);
  const menuRef = useRef(null);
  const meta = TYPE_META[a.type] || TYPE_META.general;
  const Icon = meta.Icon;

  // Open as a fixed-position dropdown (computed from the trigger rect) so the card's
  // overflow-hidden never clips it.
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 4, left: Math.max(8, r.right - 188) }); // 188 = menu width
    setMenuOpen(true);
  };

  // Close the menu on outside-click, Escape, selection, and scroll/resize.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc    = (e) => { if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return; setMenuOpen(false); };
    const onKey    = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onScroll = () => setMenuOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [menuOpen]);

  return (
    <div className="relative overflow-hidden bg-card border border-divider rounded-2xl p-4 flex flex-col gap-3">
      {/* Type accent bar */}
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${meta.accent}`} />

      {/* Top: type badge + audience pill + delete */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${meta.chip}`}>
            <Icon className="text-sm" /> {meta.label}
          </span>
          <span className="text-xs bg-nav-hover text-muted px-2 py-0.5 rounded-full flex items-center gap-1">
            <MdPeople className="text-sm" /> {(AUDIENCE_LABEL[a.targetAudience] || a.targetAudience).replace(/\bStudents\b/g, v.members)}
          </span>
        </div>
        {canDelete && (
          <button
            ref={btnRef}
            onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : 'More actions'}
            className="w-8 h-8 -mr-1 -mt-1 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdMoreHoriz className="text-lg" />
          </button>
        )}
      </div>

      {/* Overflow menu (primary admin only) — fixed so the card's overflow can't clip it */}
      {menuOpen && (
        <div
          ref={menuRef}
          role="menu"
          style={{ top: menuPos.top, left: menuPos.left }}
          className="fixed z-50 w-[188px] bg-card border border-divider rounded-xl shadow-xl py-1"
        >
          <button
            role="menuitem"
            onClick={() => { setMenuOpen(false); setConfirm(true); }}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdDelete className="text-base" /> Delete announcement
          </button>
        </div>
      )}

      {/* Body */}
      <div>
        <p className="text-base font-medium text-secondary">{a.title}</p>
        <p className="text-sm text-muted mt-1 leading-relaxed whitespace-pre-line">{a.message}</p>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-1.5 text-xs text-subtle mt-auto pt-2 border-t border-divider">
        <MdPerson className="text-sm flex-shrink-0" />
        <span className="truncate">
          by {a.createdBy?.name || 'Admin'} ·{' '}
          <span title={fmtFull(a.createdAt)}>{fmtTimestamp(a.createdAt)}</span>
        </span>
      </div>

      {/* Inline delete confirm (primary admin only) */}
      {confirmDelete && canDelete && (
        <div className="bg-nav-hover rounded-xl p-3 border border-divider flex items-center gap-2">
          <p className="text-xs text-muted flex-1">Delete this announcement?</p>
          <button
            onClick={() => { onDelete(a._id); setConfirm(false); }}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Delete
          </button>
          <button onClick={() => setConfirm(false)} className="text-xs text-muted hover:underline">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default AnnouncementCard;
